
/**
 * Cryptographic utility functions for encryption and hashing operations.
 *
 * Provides essential cryptographic capabilities including AES encryption
 * and MD5 hashing, designed as a replacement for CryptoJS functionality.
 * These utilities are used for secure data encryption with OpenSSL-compatible
 * format support.
 *
 * @module crypto
 * @author griboni@snowfly.com
 * @version 1.0.0
 */

/**
 * Computes the MD5 hash of the given input bytes.
 *
 * This implementation follows the MD5 hashing algorithm, which processes the input
 * byte array in 512-bit chunks to produce a 128-bit output hash. The function operates
 * on the input via bitwise operations and transformations to conform to the MD5 specification.
 *
 * @param {Uint8Array} bytes - The input data as a byte array to be hashed.
 * @returns {Uint8Array} A 16-byte array representing the computed MD5 hash of the input.
 */
export const md5 = (bytes) => {
    const s = [7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21];
    const k = [0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501, 0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be, 0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821, 0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa, 0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8, 0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed, 0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a, 0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c, 0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70, 0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05, 0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665, 0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92, 0xffeff47d, 0x85845dd1, 0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1, 0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391];
    let h = [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476];
    const len = bytes.length;
    const words = new Uint32Array(((len + 8) >> 6) + 1 << 4);
    for (let i = 0; i < len; i++) words[i >> 2] |= bytes[i] << ((i % 4) << 3);
    words[len >> 2] |= 0x80 << ((len % 4) << 3);
    words[words.length - 2] = len << 3;
    for (let i = 0; i < words.length; i += 16) {
        let [a, b, c, d] = h;
        for (let j = 0; j < 64; j++) {
            let f, g;
            if (j < 16) { f = (b & c) | (~b & d); g = j; }
            else if (j < 32) { f = (d & b) | (~d & c); g = (5 * j + 1) % 16; }
            else if (j < 48) { f = b ^ c ^ d; g = (3 * j + 5) % 16; }
            else { f = c ^ (b | ~d); g = (7 * j) % 16; }
            const temp = d; d = c; c = b;
            b = (b + ((a + f + k[j] + words[i + g]) << s[j] | (a + f + k[j] + words[i + g]) >>> (32 - s[j]))) | 0;
            a = temp;
        }
        h[0] = (h[0] + a) | 0; h[1] = (h[1] + b) | 0; h[2] = (h[2] + c) | 0; h[3] = (h[3] + d) | 0;
    }
    const res = new Uint8Array(16);
    for (let i = 0; i < 16; i++) res[i] = (h[i >> 2] >> ((i % 4) << 3)) & 0xff;
    return res;
}

/**
 * Encrypts plain text using AES-CBC algorithm with a password, following OpenSSL's EVP_BytestoKey method for key
 * and IV derivation. The ciphertext is returned in Base64 format with OpenSSL's required metadata.
 *
 * @param {string} plainText The plaintext string to be encrypted.
 * @param {string} password The password used for encryption.
 * @return {Promise<string>} A promise that resolves to the Base64-encoded ciphertext in OpenSSL-compatible format.
 */
export const cryptoJSEncrypt = async (plainText, password) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(plainText);
    const pass = encoder.encode(password);

    // Generate an 8-byte salt
    const salt = crypto.getRandomValues(new Uint8Array(8));

    // Derive Key (32 bytes) and IV (16 bytes) using OpenSSL EVP_BytesToKey (MD5)
    let derived = new Uint8Array(0);
    let lastHash = new Uint8Array(0);
    while (derived.length < 48) {
        const block = new Uint8Array(lastHash.length + pass.length + salt.length);
        block.set(lastHash);
        block.set(pass, lastHash.length);
        block.set(salt, lastHash.length + pass.length);
        lastHash = md5(block);
        const next = new Uint8Array(derived.length + lastHash.length);
        next.set(derived);
        next.set(lastHash, derived.length);
        derived = next;
    }

    const key = await crypto.subtle.importKey(
        'raw', derived.slice(0, 32), { name: 'AES-CBC' }, false, ['encrypt']
    );

    const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-CBC', iv: derived.slice(32, 48) }, key, data
    );

    // Build the OpenSSL format: [Magic(8 bytes)][Salt(8 bytes)][Ciphertext]
    const magic = encoder.encode("Salted__");
    const out = new Uint8Array(magic.length + salt.length + encrypted.byteLength);
    out.set(magic);
    out.set(salt, magic.length);
    out.set(new Uint8Array(encrypted), magic.length + salt.length);

    // Return as Base64 string
    return btoa(String.fromCharCode(...out));
}

// HOW TO USE:
// const encrypted = await cryptoJSEncrypt(JSON.stringify(`${u._id}-${sId}`), ENCRYPTION_KEY);
